/**
 * Default Gem Type Configurations
 * Provides sensible defaults for common gem types while allowing full customization
 */
// Helper function to create theme-aware colors
function createThemeColors(lightColor, lightBg, darkColor, darkBg) {
    return {
        light: { color: lightColor, backgroundColor: lightBg },
        dark: { color: darkColor, backgroundColor: darkBg }
    };
}
// Theme color configurations for each gem type
export const GEM_THEME_COLORS = {
    // Companies & Organizations - Blue
    'COMPANY': createThemeColors('#3b82f6', '#dbeafe', // Light: bright blue, light blue bg
    '#60a5fa', '#1e3a8a' // Dark: sky blue, dark blue bg
    ),
    // People - Purple
    'PERSON': createThemeColors('#8b5cf6', '#e9d5ff', // Light: bright purple, light purple bg
    '#a78bfa', '#5b21b6' // Dark: light purple, dark purple bg
    ),
    // Initiatives - Green
    'INITIATIVE': createThemeColors('#10b981', '#d1fae5', // Light: bright green, light green bg
    '#34d399', '#065f46' // Dark: emerald, dark green bg
    ),
    // Tools & Technology - Amber
    'TECH': createThemeColors('#f59e0b', '#fef3c7', // Light: bright amber, light amber bg
    '#fbbf24', '#78350f' // Dark: yellow, dark amber bg
    ),
    // Insights & Patterns - Orange
    'INSIGHT': createThemeColors('#f97316', '#fed7aa', // Light: bright orange, light orange bg
    '#fb923c', '#7c2d12' // Dark: light orange, dark orange bg
    ),
    // Assumptions & Questions - Red
    'ASSUMPTION': createThemeColors('#ef4444', '#fecaca', // Light: bright red, light red bg
    '#f87171', '#7f1d1d' // Dark: light red, dark red bg
    ),
    // Action Items - Pink
    'ACTION_ITEM': createThemeColors('#ec4899', '#fbcfe8', // Light: bright pink, light pink bg
    '#f472b6', '#831843' // Dark: light pink, dark pink bg
    ),
    // Decisions - Indigo
    'DECISION': createThemeColors('#6366f1', '#c7d2fe', // Light: bright indigo, light indigo bg
    '#818cf8', '#3730a3' // Dark: light indigo, dark indigo bg
    ),
    // Risks & Issues - Red (darker variant)
    'RISK_ISSUE': createThemeColors('#dc2626', '#fecaca', // Light: red, light red bg
    '#ef4444', '#991b1b' // Dark: bright red, dark red bg
    ),
    // Opportunities - Emerald
    'OPPORTUNITY': createThemeColors('#059669', '#a7f3d0', // Light: emerald, light emerald bg
    '#10b981', '#064e3b' // Dark: bright emerald, dark emerald bg
    )
};
export const DEFAULT_GEM_TYPE_CONFIGS = {
    // Companies & Organizations
    'COMPANY': {
        displayName: '🏢 Companies',
        description: 'Business entities, partners, and organizations mentioned',
        color: GEM_THEME_COLORS['COMPANY'].light.color,
        backgroundColor: GEM_THEME_COLORS['COMPANY'].light.backgroundColor,
        emoji: '🏢',
        icon: 'building',
        priority: 1,
        collapsible: true,
        maxItems: 10,
        sortBy: 'title'
    },
    // People
    'PERSON': {
        displayName: '👤 People',
        description: 'Individuals mentioned in the session',
        color: GEM_THEME_COLORS['PERSON'].light.color,
        backgroundColor: GEM_THEME_COLORS['PERSON'].light.backgroundColor,
        emoji: '👤',
        icon: 'user',
        priority: 2,
        collapsible: true,
        maxItems: 15,
        sortBy: 'title'
    },
    // Initiatives
    'INITIATIVE': {
        displayName: '🚀 Initiatives',
        description: 'Strategic projects and business initiatives',
        color: GEM_THEME_COLORS['INITIATIVE'].light.color,
        backgroundColor: GEM_THEME_COLORS['INITIATIVE'].light.backgroundColor,
        emoji: '🚀',
        icon: 'rocket',
        priority: 3,
        collapsible: true,
        maxItems: 8,
        sortBy: 'priority'
    },
    // Tools & Technology
    'TECH': {
        displayName: '🔧 Technology',
        description: 'Technical tools, platforms, and technologies',
        color: GEM_THEME_COLORS['TECH'].light.color,
        backgroundColor: GEM_THEME_COLORS['TECH'].light.backgroundColor,
        emoji: '🔧',
        icon: 'tool',
        priority: 4,
        collapsible: true,
        maxItems: 12,
        sortBy: 'title'
    },
    // Insights & Patterns
    'INSIGHT': {
        displayName: '💡 Insights',
        description: 'Key insights, patterns, and observations',
        color: GEM_THEME_COLORS['INSIGHT'].light.color,
        backgroundColor: GEM_THEME_COLORS['INSIGHT'].light.backgroundColor,
        emoji: '💡',
        icon: 'lightbulb',
        priority: 5,
        collapsible: true,
        maxItems: 10,
        sortBy: 'relevance'
    },
    // Assumptions & Questions
    'ASSUMPTION': {
        displayName: '❓ Assumptions',
        description: 'Assumptions made and questions raised',
        color: GEM_THEME_COLORS['ASSUMPTION'].light.color,
        backgroundColor: GEM_THEME_COLORS['ASSUMPTION'].light.backgroundColor,
        emoji: '❓',
        icon: 'question',
        priority: 6,
        collapsible: true,
        maxItems: 8,
        sortBy: 'importance'
    },
    // Action Items
    'ACTION_ITEM': {
        displayName: '✅ Action Items',
        description: 'Tasks and action items identified',
        color: GEM_THEME_COLORS['ACTION_ITEM'].light.color,
        backgroundColor: GEM_THEME_COLORS['ACTION_ITEM'].light.backgroundColor,
        emoji: '✅',
        icon: 'check',
        priority: 7,
        collapsible: true,
        maxItems: 15,
        sortBy: 'next_step'
    },
    // Decisions
    'DECISION': {
        displayName: '⚖️ Decisions',
        description: 'Key decisions made during the session',
        color: GEM_THEME_COLORS['DECISION'].light.color,
        backgroundColor: GEM_THEME_COLORS['DECISION'].light.backgroundColor,
        emoji: '⚖️',
        icon: 'balance',
        priority: 8,
        collapsible: true,
        maxItems: 6,
        sortBy: 'importance'
    },
    // Risks & Issues
    'RISK_ISSUE': {
        displayName: '⚠️ Risks & Issues',
        description: 'Potential risks and issues identified',
        color: GEM_THEME_COLORS['RISK_ISSUE'].light.color,
        backgroundColor: GEM_THEME_COLORS['RISK_ISSUE'].light.backgroundColor,
        emoji: '⚠️',
        icon: 'warning',
        priority: 9,
        collapsible: true,
        maxItems: 8,
        sortBy: 'severity'
    },
    // Opportunities
    'OPPORTUNITY': {
        displayName: '🎯 Opportunities',
        description: 'Business opportunities and potential areas',
        color: GEM_THEME_COLORS['OPPORTUNITY'].light.color,
        backgroundColor: GEM_THEME_COLORS['OPPORTUNITY'].light.backgroundColor,
        emoji: '🎯',
        icon: 'target',
        priority: 10,
        collapsible: true,
        maxItems: 10,
        sortBy: 'potential'
    }
};
// Theme colors for fallback configuration
export const FALLBACK_THEME_COLORS = createThemeColors('#64748b', '#e2e8f0', // Light: slate, light slate bg
'#94a3b8', '#334155' // Dark: light slate, dark slate bg
);
// Fallback configuration for unknown/custom types
export const FALLBACK_GEM_TYPE_CONFIG = {
    displayName: '',
    description: 'Custom gem type',
    color: FALLBACK_THEME_COLORS.light.color,
    backgroundColor: FALLBACK_THEME_COLORS.light.backgroundColor,
    emoji: '💎',
    icon: 'gem',
    priority: 999,
    collapsible: true,
    maxItems: 20,
    sortBy: 'id'
};
// Common field mappings for different data structures
export const FIELD_MAPPINGS = {
    title: ['title', 'name', 'text', 'summary'],
    description: ['description', 'summary', 'quote'],
    action: ['next_step', 'action', 'todo', 'task'],
    source: ['citation_id', 'source', 'reference'],
    originalLanguage: ['title_org_language', 'original_title', 'native_title']
};
// Priority order for gem types (lower number = higher priority)
export const DEFAULT_PRIORITY_ORDER = [
    'RISK_ISSUE',
    'ASSUMPTION',
    'QUESTION',
    'ACTION_ITEM',
    'DECISION',
    'INSIGHT',
    'OPPORTUNITY',
    'INITIATIVE',
    'COMPANY',
    'PERSON',
    'TECH',
];
// Theme-aware colors for dynamic gem types
export const DYNAMIC_GEM_THEME_COLORS = [
    createThemeColors('#06b6d4', '#cffafe', '#22d3ee', '#083344'),
    createThemeColors('#8b5cf6', '#e9d5ff', '#a78bfa', '#5b21b6'),
    createThemeColors('#10b981', '#d1fae5', '#34d399', '#065f46'),
    createThemeColors('#f59e0b', '#fef3c7', '#fbbf24', '#78350f'),
    createThemeColors('#f97316', '#fed7aa', '#fb923c', '#7c2d12'),
    createThemeColors('#ef4444', '#fecaca', '#f87171', '#7f1d1d'),
    createThemeColors('#ec4899', '#fbcfe8', '#f472b6', '#831843'),
    createThemeColors('#6366f1', '#c7d2fe', '#818cf8', '#3730a3'),
    createThemeColors('#84cc16', '#ecfccb', '#a3e635', '#365314'),
    createThemeColors('#14b8a6', '#ccfbf1', '#2dd4bf', '#134e4a'),
    createThemeColors('#a855f7', '#e9d5ff', '#c084fc', '#6b21a8'),
    createThemeColors('#f472b6', '#fce7f3', '#f9a8d4', '#831843'),
    createThemeColors('#3b82f6', '#dbeafe', '#60a5fa', '#1e3a8a'),
    createThemeColors('#22c55e', '#dcfce7', '#4ade80', '#166534'),
    createThemeColors('#eab308', '#fef08a', '#facc15', '#713f12'),
    createThemeColors('#64748b', '#e2e8f0', '#94a3b8', '#334155'),
    createThemeColors('#7c3aed', '#ddd6fe', '#a855f7', '#5b21b6'),
    createThemeColors('#dc2626', '#fee2e2', '#ef4444', '#991b1b'),
    createThemeColors('#059669', '#a7f3d0', '#10b981', '#064e3b'),
    createThemeColors('#92400e', '#fed7aa', '#d97706', '#451a03') // Brown
];
// Legacy support for non-theme-aware code
export const DYNAMIC_GEM_COLORS = DYNAMIC_GEM_THEME_COLORS.map(colors => colors.light);
// Get theme-aware color for dynamic gem type by index
export function getDynamicGemThemeColor(index) {
    return DYNAMIC_GEM_THEME_COLORS[index % DYNAMIC_GEM_THEME_COLORS.length];
}
// Get color for dynamic gem type by index (legacy support)
export function getDynamicGemColor(index) {
    return DYNAMIC_GEM_COLORS[index % DYNAMIC_GEM_COLORS.length];
}
